// ---------------------------------------------------------------------------
// WHAT KIND OF GREEN — turning the scenery layer's counts into the words on a
// card.
//
// THE PROBLEM THIS SOLVES. Every green thing a route passes currently reaches
// the user as one of two words. `src/api/osm.ts` folds farmland, meadow,
// orchard, vineyard, forest and actual parks into a single number called
// `parks`, and `routeReason.ts` turns any non-zero value into "some greenery".
// So these two drives, measured Aug 17 on our own data, read identically:
//
//   Perugia → Foligno   97 farm fields · 110 orchards · 75 woods · 83 historic
//   Taconic Parkway     218 woods · 148 water · 28 fields · 6 historic
//
// One is Umbria in August. The other is a parkway through the Hudson Valley.
// "Some greenery" is not wrong about either, and it is useless about both.
//
// RANKED BY EXTENT, NOT COUNT. Forty grass verges are not a landscape and one
// forest is. `size_m` (length for lines, perimeter for areas, computed at
// import) is a rough proxy for how much of a thing there is — rough because a
// hundred small fields have more perimeter than their area deserves. It is the
// honest measure we have; if it ever reads wrong on a real drive, that is a
// reason to store area, not a reason to guess.
//
// SAYS NOTHING RATHER THAN SOMETHING FALSE. Returns null when the route is
// unmeasured — NOT "no greenery". An uncovered region has to stay
// distinguishable from an empty one all the way to the sentence, or rule 20's
// defect just reappears in the copy instead of the score.
// ---------------------------------------------------------------------------

/** Counts + extent per coarse category, straight from
 *  `scenic_summary_near_path`. */
export interface ScenicSummary {
  farmland_n: number; farmland_m: number;
  orchard_n: number;  orchard_m: number;
  meadow_n: number;   meadow_m: number;
  wood_n: number;     wood_m: number;
  park_n: number;     park_m: number;
  water_n: number;    water_m: number;
  viewpoint_n: number;
  historic_n: number;
  total_n: number;
}

/**
 * The words themselves. EDIT THIS TABLE, not the logic below — it is the only
 * part of this file that is about voice rather than arithmetic.
 *
 * Plain nouns a driver would use looking out of a window. Not "agricultural
 * land", not "verdant countryside". Morgan's standard: honest, concrete, never
 * pitchy.
 */
export const GREEN_WORDS = {
  farmland: "open farmland",
  orchard: "orchards",
  meadow: "meadows",
  wood: "woods",
  park: "parkland",
} as const;

export type GreenBucket = keyof typeof GREEN_WORDS;

/** A category has to be this share of the route's green to get named. Below
 *  it, mentioning it overstates a hedge into a landscape. */
const SHARE_TO_NAME = 0.15;
/** And it needs to be more than one lonely polygon — a single field beside a
 *  motorway junction is not "open farmland". */
const MIN_FEATURES = 3;
/** Two nouns is a description; three is a list. */
const MAX_NAMED = 2;
/**
 * TIERS, NOT WEIGHTS. Open country and destinations outrank woodland — always,
 * not on a tie.
 *
 * This is the SAME tiering `fetchGreenCorridorViasFromLayer` already applies
 * when it picks the road (DESTINATION and OPEN_COUNTRY at tier 0, WOODLAND at
 * tier 1), so the card and the router cannot disagree about what a drive is
 * for. Morgan, Aug 13: "i want farmland to rank high everywhere… what made
 * umbria's streets great were not rivers - they were fields of sunflowers and
 * open space and views of medieval cities."
 *
 * I first shipped this as a 1.5x multiplier on open country, which still let
 * woods win in Umbria on raw extent. Morgan, Aug 17: "why would woods be
 * better than farmland? no." She is right, and the earlier version was me
 * splitting a difference that does not need splitting — the rule was already
 * written down in scenicLayer.ts and I made up a softer one beside it.
 *
 * Only the BEST tier present gets named, exactly as the via-picker does. A
 * route past real fields is described by its fields; a route through nothing
 * but forest is still honestly "woods", because tier 1 is what remains when
 * tier 0 has nothing worth mentioning.
 */
const TIER: Record<GreenBucket, number> = {
  farmland: 0,
  orchard: 0,
  meadow: 0,
  park: 0,
  wood: 1,
};

export interface GreenPhraseInput {
  summary: ScenicSummary | null;
  /** From `scenic_coverage_for_path`. Below `minCovered` we say nothing at
   *  all, because we haven't looked at enough of this drive to describe it. */
  coveredFraction: number;
  minCovered?: number;
}

/**
 * The ranked categories worth naming, biggest first. Empty when the route is
 * unmeasured or has nothing worth claiming.
 */
export function rankGreen(input: GreenPhraseInput): GreenBucket[] {
  const { summary, coveredFraction, minCovered = 0.5 } = input;
  if (!summary) return [];
  if (!(coveredFraction >= minCovered)) return [];

  const buckets: { key: GreenBucket; n: number; m: number }[] = [
    { key: "farmland", n: summary.farmland_n, m: summary.farmland_m },
    { key: "orchard",  n: summary.orchard_n,  m: summary.orchard_m },
    { key: "meadow",   n: summary.meadow_n,   m: summary.meadow_m },
    { key: "wood",     n: summary.wood_n,     m: summary.wood_m },
    { key: "park",     n: summary.park_n,     m: summary.park_m },
  ];
  const totalM = buckets.reduce((s, b) => s + (b.m || 0), 0);
  if (totalM <= 0) return [];

  // Thresholds are judged on TRUE share, before tiering. A hedge does not
  // become a landscape by being the right kind of hedge.
  const worth = buckets.filter(
    (b) => b.n >= MIN_FEATURES && b.m / totalM >= SHARE_TO_NAME,
  );
  if (worth.length === 0) return [];

  const bestTier = Math.min(...worth.map((b) => TIER[b.key]));
  return worth
    .filter((b) => TIER[b.key] === bestTier)
    .sort((a, b) => b.m - a.m)
    .slice(0, MAX_NAMED)
    .map((b) => b.key);
}

/** Join two nouns the way a person would. */
function joinWords(words: string[]): string {
  if (words.length === 0) return "";
  if (words.length === 1) return words[0];
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/**
 * The phrase, with no leading preposition, ready to drop into a sentence:
 *
 *   `Mostly the fast route — leaves it midway for ${phrase}`
 *   `Passes ${phrase}`
 *
 * Returns null when there is nothing honest to say — the caller keeps its
 * existing wording ("quieter streets") rather than inventing scenery.
 */
export function greenPhrase(input: GreenPhraseInput): string | null {
  const ranked = rankGreen(input);
  if (ranked.length === 0) return null;
  return joinWords(ranked.map((k) => GREEN_WORDS[k]));
}

/**
 * Is this route's green worth mentioning at all? Replaces the
 * `(f?.parks ?? 0) > 0` test in routeReason.ts, which was true for a single
 * traffic island.
 */
export function hasNamableGreen(input: GreenPhraseInput): boolean {
  return rankGreen(input).length > 0;
}

// ---------------------------------------------------------------------------
// NAMED FEATURES — the second half of the sentence.
//
// A name is a CLAIM, and the whole point of the gate behind it is that
// proximity cannot tell driving beside a river from crossing it once. The
// database decides what may be claimed (scenic_named_near_path); this only
// decides how it reads.
//
// Morgan's rules, Aug 17:
//   · two kilometres of the route beside or inside a thing before "along" or
//     "through" may be said — the Fiume Chiascio manages 924m on the
//     Perugia-Foligno road and therefore does NOT get named, which is the bar
//     doing its job on the first real test;
//   · landmarks are gated on distance instead, because you cannot run two
//     kilometres beside a castle;
//   · only the big categories — no streams, no canals (747 named streams in
//     one Umbrian bounding box, almost all ditches);
//   · at most two names;
//   · the kind of green comes first and the name second.
// ---------------------------------------------------------------------------

export interface NamedFeature {
  /** 'through' (inside it for 2km+), 'along' (beside it for 2km+), 'past' (a
   *  landmark within the distance gate). Never null — the RPC drops anything
   *  that cannot be claimed. */
  claim: "through" | "along" | "past";
  kind: string;
  subkind: string;
  name: string;
  dist_m: number;
  along_m: number;
  is_point: boolean;
}

const MAX_NAMES = 2;

/**
 * "through Parco Regionale del Monte Subasio", "along the Hudson, past
 * Olana", or null when nothing qualifies.
 *
 * NO DEFINITE ARTICLE is inserted. "past the Basilica di Santa Chiara" reads
 * better in English than "past Basilica di Santa Chiara" — but the same rule
 * produces "through the Parco Regionale del Monte Subasio", which does not,
 * and guessing an article per language is how copy starts sounding machine-
 * made. Names go in as their mappers wrote them.
 */
/**
 * THE SAME PLACE, DESIGNATED THREE TIMES.
 *
 * The Carpentras-Sault road returned, in order:
 *   through Parc naturel regional du Mont-Ventoux                       28,774m
 *   through Aire de cooperation de la reserve de biosphere du Mont Ventoux 26,353m
 *   through Zone tampon de la reserve de biosphere du Mont Ventoux       12,672m
 *
 * All three are true, all three are the same mountain, and a card that names
 * the first two tells a driver one useful thing and one piece of administrative
 * paperwork. Protected areas nest - a park inside a biosphere reserve inside
 * its own buffer zone - so this is the normal case in Europe, not an oddity.
 *
 * The rule: once a name is taken, skip any later name that shares two or more
 * distinctive words with it. "Mont" + "Ventoux" is two, so both duplicates go.
 * A route genuinely passing two different places keeps both, because unrelated
 * names do not share two long words by accident.
 *
 * Deliberately NOT clever. No stemming, no fuzzy distance - those fail in ways
 * that are hard to see and impossible to explain when a card reads oddly.
 */
const STOP_WORDS = new Set([
  "parc", "park", "parco", "parque", "national", "nationale", "nationaal",
  "naturel", "naturelle", "natural", "naturale", "regional", "regionale",
  "reserve", "riserva", "reserva", "zone", "zona", "aire", "area", "de", "du",
  "des", "la", "le", "les", "del", "della", "di", "the", "of", "and", "et",
  "forest", "foret", "foresta", "bosco", "wood", "woods", "state", "county",
]);

/** Long, non-generic words in a name, lowercased and stripped of accents. */
function distinctiveWords(name: string): Set<string> {
  const norm = name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ");
  return new Set(
    norm.split(/\s+/).filter((w) => w.length >= 4 && !STOP_WORDS.has(w)),
  );
}

function sharesTwoWords(a: Set<string>, b: Set<string>): boolean {
  let n = 0;
  for (const w of a) if (b.has(w) && ++n >= 2) return true;
  return false;
}

export function namedPhrase(features: NamedFeature[] | null | undefined): string | null {
  if (!features || features.length === 0) return null;
  // Dedupe BEFORE truncating to MAX_NAMES. Taking the first two and then
  // deduping would leave one name where two distinct ones were available.
  const take: NamedFeature[] = [];
  const seen: Set<string>[] = [];
  for (const f of features) {
    if (take.length >= MAX_NAMES) break;
    const words = distinctiveWords(f.name);
    if (seen.some((s) => sharesTwoWords(words, s))) continue;
    take.push(f);
    seen.push(words);
  }
  if (take.length === 0) return null;

  // Group consecutive same-claim names so we say "through A and B", not
  // "through A, through B".
  const parts: string[] = [];
  let i = 0;
  while (i < take.length) {
    const claim = take[i].claim;
    const names: string[] = [];
    while (i < take.length && take[i].claim === claim) { names.push(take[i].name); i++; }
    parts.push(`${claim} ${names.join(" and ")}`);
  }
  return parts.join(", ");
}

/**
 * The whole scenery clause: what kind of drive it is, then what you'll see.
 * Morgan, Aug 17 — "both, name second".
 *
 *   "open farmland and orchards, through Parco Regionale del Monte Subasio"
 *
 * Either half may be null; so may the result, which means the caller keeps
 * whatever it says today rather than inventing scenery.
 */
export function sceneryClause(
  green: string | null,
  named: string | null,
): string | null {
  if (green && named) return `${green}, ${named}`;
  return green ?? named ?? null;
}
